import { BUTTON_STYLES, SHAPE_STYLES } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  anchoredPos,
  getAbsolutePos,
  getAbsolutePosAndScale,
  getZeroAnchoredEntity,
  getZeroEntity,
  setEntityPosition,
  setParent,
} from './entity';
import { lerpNumber, v2 } from './helpers';
import { createAndAddLocalIconButton } from './icon_button';
import { createAndAddLocalImageObject } from './image_object';
import { createAndAddLocalContainer } from './container';
import { cacheImage } from './controllers/data_controller';
import { t } from './localizationController';
import { setVisibilityState } from './simulate';
import { ObjectType } from './shared/api_shared_types';
import {
  Container,
  Entity,
  GameManager,
  GameObjectController,
  MoveMode,
  PhotoChoice,
  Shape,
  VisibilityState,
  type PhotoChoiceEntry,
} from './types';
import { dt } from '.';
import logger from './controllers/logger';

// Layout / feedback tuning (whole panel sized to ~1/3 of the original)
const MAX_PHOTOS = 6;
const PANEL_ANCHOR_X = 0.72; //horizontal placement in the instruction layer (0.5 = center, >0.5 = right)
const PHOTO_MAX_EM = 2; //max image size (em) inside the chooser (~2/3 of the previous size)
const PHOTO_GAP_PX = -20; //horizontal gap between photos
const BUTTON_GAP_PX = 80; //gap below the row for the complete button

// Backdrop box hugging the chooser content
const BACKDROP_PAD_X = 60; //horizontal padding around the photo row
const BACKDROP_PAD_Y = 0; //vertical padding above/below the content
const BACKDROP_BUTTON_ALLOWANCE = 120; //room below the complete button's center for its body
const BACKDROP_CORNER = 40; //corner radius of the backdrop box (button size unchanged)
const SELECT_SCALE = 1.15; //selected photos enlarge to this
const SELECT_LIFT_PX = 27; //selected photos move up this many px
const SELECT_LERP = 12; //per-second lerp strength for select feedback
const SELECT_EPS = 0.001; //"settled" threshold

// Bottom-drawer peek/hover behavior
const DRAWER_PEEK_FRACTION = 0.25; //fraction of the content box visible in the idle (docked) position
const DRAWER_ACTIVE_MARGIN = 0; //gap from the screen bottom when raised (0 = box bottom flush with screen bottom, so no dead strip retriggers the lower)
const DRAWER_LERP = 10; //per-second lerp strength for the raise/lower animation
const DRAWER_EPS = 0.5; //"settled" threshold (px)

// Fly-to-canvas (on complete)
const FLY_DURATION = 0.6;
const FLY_LERP = 8;
const CANVAS_PHOTO_EM = 7; //match the startup scatter (wishcollector_prototype1.ts)
const SCATTER_CENTER_X = 800;
const SCATTER_CENTER_Y = 0;
const SCATTER_WIDTH = 2000;
const SCATTER_HEIGHT = 2000;
const SCATTER_MAX_ROTATION = 0.08;

export function controller(): GameObjectController {
  return {
    getNew: getZeroPhotoChoice,
    sync: null,
    update,
    createPackage,
    dispose,
  };
}

export function getZeroPhotoChoice(ent: Entity): PhotoChoice {
  const photoChoice: PhotoChoice = {
    ...ent,
    objectType: ObjectType.PHOTO_CHOICE,
    entries: [],
    completeButton: null,
    backdrop: null,
    boxHeight: 0,
    boxCenterY: 0,
    drawerSettled: false,
    raiseSuppressed: false,
    isCompleting: false,
    completeElapsed: null,
    flying: [],
  };

  photoChoice.shape = Shape.RECT;
  photoChoice.allowDrag = false;
  photoChoice.allowHighlight = false;

  return photoChoice;
}

export function createPhotoChoice(manager: GameManager, imageIds: string[]): PhotoChoice {
  const ids = imageIds.slice(0, MAX_PHOTOS);

  const photoChoice = getZeroPhotoChoice(getZeroStaticPanelEntity());
  addGameObject(manager, photoChoice);

  setParent(photoChoice, manager.mainRoom.instructionLayer, v2());

  const spacing = PHOTO_MAX_EM * manager.rootUnitSizePx + PHOTO_GAP_PX;

  // Semi-transparent box sized to hug the chooser content (the fanned photo row + complete button),
  // parented to the panel so it fades in/out and cleans up with it.
  const unit = manager.rootUnitSizePx;
  const photoCardHalf = PHOTO_MAX_EM * unit * 0.7; //~half a chooser card incl. the caption strip
  const maxHomeX = ((ids.length - 1) / 2) * spacing;
  const maxHomeY = Math.abs(maxHomeX) * 0.2; //outer photos droop by this in the fan layout
  const buttonCenterY = (PHOTO_MAX_EM * unit) / 2 + BUTTON_GAP_PX;

  const contentTop = -photoCardHalf - SELECT_LIFT_PX; //photos lift up when selected
  const contentBottom = Math.max(maxHomeY + photoCardHalf, buttonCenterY + BACKDROP_BUTTON_ALLOWANCE);
  const boxWidth = maxHomeX * 2 + PHOTO_MAX_EM * unit + BACKDROP_PAD_X * 2;
  const boxHeight = contentBottom - contentTop + BACKDROP_PAD_Y * 2;
  const boxCenterY = (contentTop + contentBottom) / 2;

  const backdropEnt = getZeroAnchoredEntity();
  backdropEnt.width = boxWidth;
  backdropEnt.height = boxHeight;
  backdropEnt.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, boxCenterY), v2(0.5, 0.5));
  const backdrop = createAndAddLocalContainer(manager, backdropEnt, {
    ...SHAPE_STYLES.NONE,
    fillColor: COLORS.BLACK_50,
  });
  backdrop.cornerRadius = BACKDROP_CORNER;
  backdrop.allowDrag = false;
  backdrop.allowHighlight = false;
  backdrop.clickable = true; //absorb clicks so the box behaves as a modal backdrop
  setParent(backdrop, photoChoice);
  photoChoice.backdrop = backdrop;
  photoChoice.boxHeight = boxHeight;
  photoChoice.boxCenterY = boxCenterY;

  // Build the chooser photos at the same size as a canvas photo, then shrink the whole card
  // uniformly with baseScale. This keeps the padding/caption proportions identical to the
  // canvas — the chooser photo is just the canvas photo scaled (and rotated).
  const chooserScale = PHOTO_MAX_EM / CANVAS_PHOTO_EM;

  for (let i = 0; i < ids.length; i++) {
    const imageId = ids[i];
    cacheImage('images/photos/' + imageId + '.JPG', imageId);

    const imageObject = createAndAddLocalImageObject(manager, getZeroAnchoredEntity(), imageId, {
      maxImageWidthEm: CANVAS_PHOTO_EM,
      maxImageHeightEm: CANVAS_PHOTO_EM,
      baseScale: chooserScale,
      label: t('photo.' + imageId + '.title'),
      appearDuration: 0.4,
      rotation: -0.2 + i * (0.4 / (ids.length - 1)),
    });
    // Selectable-only: clickable (inherited) but not draggable in the chooser.
    imageObject.allowDrag = false;

    const homeX = (i - (ids.length - 1) / 2) * spacing;

    const homeY = Math.abs(homeX) * 0.2;
    imageObject.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(homeX, homeY), v2(0.5, 0.5));

    setParent(imageObject, photoChoice);

    const entry: PhotoChoiceEntry = {
      imageObject,
      imageId,
      isSelected: false,
      homeX,
      homeY,
      selectLift: 0,
    };
    photoChoice.entries.push(entry);
  }

  const applyStyle = {
    ...BUTTON_STYLES.CHECK,
    icon: 'icon_check',
    color: COLORS.GREEN_DARK,
    colorHighlight: COLORS.GREEN_DARK_HIGHLIGHT,
  };
  const button = createAndAddLocalIconButton(manager, applyStyle);
  button.moveMode = MoveMode.ANCHORED;
  button.allowDrag = false;
  button.alwaysOnTop = true; //keep the apply button on the foreground
  const belowY = (PHOTO_MAX_EM * manager.rootUnitSizePx) / 2 + BUTTON_GAP_PX;
  button.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, belowY), v2(0.5, 0.5));
  setParent(button, photoChoice);
  photoChoice.completeButton = button;

  setVisibilityState(photoChoice, VisibilityState.APPEARING);

  return photoChoice;
}

// Full-screen anchored panel seed for the chooser, shifted toward the right of the layer.
function getZeroStaticPanelEntity(): Entity {
  const ent = getZeroAnchoredEntity();
  ent.anchoredPos = anchoredPos(v2(PANEL_ANCHOR_X, 0.5), v2(0, 0), v2(0.5, 0.5));
  ent.relativeWidth = 1;
  ent.relativeHeight = 1;
  return ent;
}

// Bottom-drawer behavior: docked so only the top DRAWER_PEEK_FRACTION of the content box shows;
// raises to the fully-visible 'active' position while the cursor is over the box, lowers otherwise.
function updateDrawer(pc: PhotoChoice): boolean {
  const layerHeight = pc.height;
  if (layerHeight <= 0) {
    return false;
  }

  const idleOffsetY =
    layerHeight / 2 - pc.boxCenterY + (0.5 - DRAWER_PEEK_FRACTION) * pc.boxHeight;
  const activeOffsetY = layerHeight / 2 - DRAWER_ACTIVE_MARGIN - pc.boxCenterY - pc.boxHeight / 2;

  // Snap to the docked position on the first frame so it doesn't slide up from screen center.
  if (!pc.drawerSettled) {
    pc.anchoredPos.offset.y = idleOffsetY;
    pc.drawerSettled = true;
    return true;
  }

  const isHovered = pc.backdrop ? pc.backdrop.mouseOver : false;

  // Once the complete button is pressed the drawer is suppressed: it stays docked (ignoring hover)
  // until the cursor leaves the box, so it only rises again on a fresh hover-in.
  if (pc.raiseSuppressed && !isHovered) {
    pc.raiseSuppressed = false;
  }

  const canRaise = isHovered && !pc.raiseSuppressed && !pc.isCompleting;
  const targetY = canRaise ? activeOffsetY : idleOffsetY;
  const t = Math.min(1, DRAWER_LERP * dt);
  pc.anchoredPos.offset.y = lerpNumber(pc.anchoredPos.offset.y, targetY, t);

  return Math.abs(pc.anchoredPos.offset.y - targetY) > DRAWER_EPS;
}

function update(pc: PhotoChoice): boolean {
  // Runs even while completing so the drawer retracts to idle as the photos fly out.
  const drawerDirty = updateDrawer(pc);

  if (pc.isCompleting) {
    return updateCompleting(pc) || drawerDirty;
  }

  let isDirty = drawerDirty;

  for (const entry of pc.entries) {
    const imageObject = entry.imageObject;

    if (imageObject.clickedThisFrame) {
      entry.isSelected = !entry.isSelected;
    }

    // ImageObject recomputes its own scale each frame from imageEnt.scale * (1 + liftScale)
    // and lerps liftScale toward liftScaleTarget internally, so drive the enlarge through that.
    imageObject.liftScaleTarget = entry.isSelected ? SELECT_SCALE - 1 : 0;

    const targetLift = entry.isSelected ? -SELECT_LIFT_PX : 0;
    const t = Math.min(1, SELECT_LERP * dt);
    entry.selectLift = lerpNumber(entry.selectLift, targetLift, t);
    imageObject.anchoredPos.offset.y = entry.homeY + entry.selectLift;

    if (
      Math.abs(entry.selectLift - targetLift) > SELECT_EPS ||
      Math.abs(imageObject.liftScale - imageObject.liftScaleTarget) > SELECT_EPS
    ) {
      isDirty = true;
    }
  }

  if (pc.completeButton && pc.completeButton.clickedThisFrame) {
    beginComplete(pc);
    isDirty = true;
  }

  return isDirty;
}

function beginComplete(pc: PhotoChoice): void {
  const manager = pc.manager;
  const canvas = manager.mainRoom ? manager.mainRoom.canvas : null;

  pc.isCompleting = true;
  pc.completeElapsed = 0;
  pc.raiseSuppressed = true; //dock the drawer and keep it down until the cursor re-enters

  if (pc.completeButton) {
    setVisibilityState(pc.completeButton, VisibilityState.DISAPPEARING);
    pc.completeButton = null;
  }

  if (!canvas) {
    return;
  }

  const [layerPos, layerScale] = getAbsolutePosAndScale(canvas.layer);

  for (const entry of pc.entries) {
    if (!entry.isSelected) {
      setVisibilityState(entry.imageObject, VisibilityState.DISAPPEARING);
      continue;
    }

    // Convert the chooser photo's on-screen position into canvas-layer-local space
    // so the spawned polaroid starts exactly where the chooser photo sits.
    const screenPos = getAbsolutePos(entry.imageObject);
    const startX = (screenPos.x - layerPos.x) / layerScale;
    const startY = (screenPos.y - layerPos.y) / layerScale;

    const photoObj = createAndAddLocalImageObject(manager, getZeroEntity(), entry.imageId, {
      maxImageWidthEm: CANVAS_PHOTO_EM,
      maxImageHeightEm: CANVAS_PHOTO_EM,
      label: t('photo.' + entry.imageId + '.title'),
      appearDuration: 0,
      influenceKinetic: true,
      moveMode: MoveMode.KINETIC,
    });
    canvas.attachChild(photoObj, canvas);
    setEntityPosition(photoObj, startX, startY);

    const rx = Math.random();
    const ry = Math.random();
    const rot = (Math.random() - 0.5) * 2 * SCATTER_MAX_ROTATION;
    photoObj.rotation = rot;
    photoObj.baseRotation = rot;

    const targetX = SCATTER_CENTER_X + (rx - 0.5) * SCATTER_WIDTH;
    const targetY = SCATTER_CENTER_Y + (ry - 0.5) * SCATTER_HEIGHT;

    pc.flying.push({ photoObj, targetX, targetY });

    // Hide the chooser copy; the flying polaroid takes its place.
    setVisibilityState(entry.imageObject, VisibilityState.DISAPPEARING);
  }
}

function updateCompleting(pc: PhotoChoice): boolean {
  if (pc.completeElapsed === null) {
    return false;
  }

  pc.completeElapsed += dt;
  const t = Math.min(1, FLY_LERP * dt);

  for (const fly of pc.flying) {
    const offset = fly.photoObj.anchoredPos.offset;
    offset.x = lerpNumber(offset.x, fly.targetX, t);
    offset.y = lerpNumber(offset.y, fly.targetY, t);
    fly.photoObj.vel.x = 0;
    fly.photoObj.vel.y = 0;
  }

  if (pc.completeElapsed >= FLY_DURATION) {
    for (const fly of pc.flying) {
      fly.photoObj.anchoredPos.offset.x = fly.targetX;
      fly.photoObj.anchoredPos.offset.y = fly.targetY;
    }
    pc.flying = [];
    pc.completeElapsed = null;
    pc.disappearDuration = 0.2;
    setVisibilityState(pc, VisibilityState.DISAPPEARING);
    if (pc.backdrop) {
      pc.backdrop.disappearDuration = 0.2;
      setVisibilityState(pc.backdrop, VisibilityState.DISAPPEARING);
    }
    return false;
  }

  return true;
}

export function createPackage(): [] {
  // Invisible grouping node — children render themselves.
  return [];
}

function dispose(_pc: PhotoChoice): void {
  // Children are cleaned up by the hierarchy.
}
